import { useTabStore, TAB_SCOPES, type Tab, type TabScope, type TabType } from '../store/tabStore';
import { useThemeStore } from '../store/themeStore';
import { useNetworkUIStore } from '../store/networkUIStore';
import { useAlertStore } from '../store/alertStore';
import { useZoneStore } from '../store/zoneStore';
import { useCouplingStore } from '../store/couplingStore';
import { NotificationCenter } from './NotificationCenter';
import { ElementSearch } from './header/ElementSearch';
import { authService } from '../services/authService';
import { useCapabilities } from '../store/featureStore';
import { isTabAvailable } from '../features/capabilities';
import { useHelpStore } from '../help/helpStore';

/**
 * Header search slot: tabs that show the zone on a map search network elements. The cross-zone
 * pages (dashboard, Netzmonitoring) carry their zone search in the page toolbar
 * (`components/shared/ZoneSearch.tsx`), the remaining pages have no search at all.
 */
function headerSearchFor(tabType: TabType | undefined): 'element' | null {
  switch (tabType) {
    case 'map':
    case 'stress':
    case 'netzanschluss':
      return 'element';
    default:
      return null;
  }
}

/** Display order of the tab groups: all zones first, then the selected zone, then the rest. */
const SCOPE_ORDER: TabScope[] = ['fleet', 'zone', 'app'];

/** Shared look of the two scope chips: filled = the scope the user is in, quiet = the other one. */
const scopeChipClass = (active: boolean) =>
  `flex items-center gap-1.5 h-[24px] pl-2 pr-2.5 rounded-md text-[10px] uppercase tracking-wider font-bold whitespace-nowrap select-none transition-colors ${
    active
      ? 'bg-accent-primary text-white shadow-sm'
      : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-primary-600/60 dark:text-primary-300 dark:hover:bg-primary-600'
  }`;

/**
 * Head of the "all zones" box. Dashboard and Netzmonitoring show every zone at once. Filled while
 * one of them is open — then the zone chip next to it is quiet, so nobody reads a zone name into
 * a page that shows the whole grid.
 */
function FleetScopeChip({ active, onClick }: { active: boolean; onClick: () => void }) {
  const zoneCount = useZoneStore(s => s.zones.length);
  return (
    <button type="button" onClick={onClick} className={scopeChipClass(active)} title="Dashboard und Netzmonitoring zeigen alle Zonen gleichzeitig">
      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
      </svg>
      Alle Zonen
      {zoneCount > 0 && <span className="font-medium opacity-75 tabular-nums">{zoneCount}</span>}
    </button>
  );
}

/**
 * Head of the zone box: names the ONE zone every tab of the box shows (map, monitoring, tables,
 * planning …) and marks a zone that is calculated within a Netzverbund. Filled only while a zone
 * tab is open.
 */
function ZoneScopeChip({ active, onClick }: { active: boolean; onClick: () => void }) {
  const zoneName = useZoneStore(s => s.zones.find(z => z.id === s.activeZoneId)?.name ?? null);
  const activeZoneId = useZoneStore(s => s.activeZoneId);
  const verbund = useCouplingStore(s => s.verbund);
  const partners = (verbund?.zones ?? []).filter(z => z.id !== activeZoneId);

  if (!activeZoneId) {
    return (
      <span
        className="flex items-center h-[24px] px-2.5 rounded-md border border-dashed border-gray-400 dark:border-primary-500 text-[11px] italic text-gray-500 dark:text-primary-400 whitespace-nowrap select-none"
        title="Karte, Monitoring und die weiteren Tabs dieser Gruppe zeigen immer genau eine Zone — wählen Sie eine im Dashboard, im Netzmonitoring oder über die Zonenauswahl."
      >
        Keine Zone gewählt
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={scopeChipClass(active)}
      title={`Karte, Monitoring und die weiteren Tabs dieser Gruppe zeigen die Zone „${zoneName ?? activeZoneId}"${partners.length > 0 ? ` — rechnet im Netzverbund mit ${partners.map(z => z.name).join(', ')}` : ''}`}
    >
      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      <span className="opacity-75">Zone</span>
      <span className="max-w-56 truncate text-xs normal-case tracking-normal font-semibold">{zoneName ?? activeZoneId}</span>
      {partners.length > 0 && (
        <span className="pl-1.5 ml-0.5 border-l border-current/30 font-medium normal-case tracking-normal opacity-80">⇄ Verbund · {partners.length + 1}</span>
      )}
    </button>
  );
}

function NotificationBell() {
  const unreadCount = useAlertStore(s => s.unreadCount);
  const isOpen = useAlertStore(s => s.isNotificationCenterOpen);
  const setOpen = useAlertStore(s => s.setNotificationCenterOpen);
  const markAllRead = useAlertStore(s => s.markAllRead);

  const handleClick = () => {
    if (!isOpen) {
      markAllRead();
    }
    setOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className="p-[7px] rounded transition-colors text-gray-600 dark:text-primary-300 hover:bg-gray-200 dark:hover:bg-primary-600"
        title="Benachrichtigungen"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && <NotificationCenter />}
    </div>
  );
}

function LogoutButton() {
  const username = authService.getUsername();
  return (
    <div className="flex items-center gap-1 px-2">
      {username && (
        <span className="text-xs text-gray-500 dark:text-primary-400 max-w-[120px] truncate">
          {username}
        </span>
      )}
      <button
        onClick={() => authService.logout()}
        className="p-[7px] rounded transition-colors text-gray-600 dark:text-primary-300 hover:bg-gray-200 dark:hover:bg-primary-600"
        title="Abmelden"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </button>
    </div>
  );
}

export const TabBar = ({ showLogout }: { showLogout?: boolean }) => {
  const { tabs, activeTabId, setActiveTab, closeTab, openTab } = useTabStore();
  const capabilities = useCapabilities();
  const hasActiveZone = useZoneStore(s => s.activeZoneId != null);
  const theme = useThemeStore(state => state.theme);
  const isControlSidebarCollapsed = useNetworkUIStore(state => state.isControlSidebarCollapsed);
  const activeTab = tabs.find(tab => tab.id === activeTabId);
  const isStressTab = activeTab?.type === 'stress';
  const sidebarWidth = isControlSidebarCollapsed ? 'w-12' : isStressTab ? 'w-80' : 'w-64';
  const headerSearch = headerSearchFor(activeTab?.type);
  // Grouped by what a tab is about (tabStore.TAB_SCOPES); inside a group the open order stays
  const groups = SCOPE_ORDER
    .map(scope => ({ scope, tabs: tabs.filter(tab => TAB_SCOPES[tab.type] === scope && isTabAvailable(tab.type, capabilities)) }))
    .filter(group => group.tabs.length > 0);

  // The scope the user is in: its box is highlighted, the other one stays quiet
  const activeScope: TabScope | null = activeTab ? TAB_SCOPES[activeTab.type] : null;

  // tone: 'normal' = tab of the scope the user is in, 'quiet' = tab of the other scope (readable,
  // but clearly the other area), 'disabled' = zone tab without a selected zone
  const renderTab = (tab: Tab, tone: 'normal' | 'quiet' | 'disabled') => {
    const isActive = tab.id === activeTabId;
    return (
      <div
        key={tab.id}
        role="tab"
        aria-selected={isActive}
        className={`
          relative flex items-center gap-2 h-full px-3.5 cursor-pointer select-none transition-colors duration-150
          ${
            isActive
              ? 'text-accent-primary dark:text-white'
              : `hover:text-gray-900 dark:hover:text-white ${
                  tone === 'disabled' ? 'text-gray-400 dark:text-white/30'
                    : tone === 'quiet' ? 'text-gray-500 dark:text-white/50'
                      : 'text-gray-700 dark:text-white/80'
                }`
          }
        `}
        onClick={() => setActiveTab(tab.id)}
      >
        <span className="text-xs uppercase tracking-wider font-bold whitespace-nowrap">{tab.title}</span>
        {isActive && <span className="absolute left-3 right-3 bottom-0 h-[2px] rounded-full bg-accent-primary" aria-hidden="true" />}

        {tab.closeable && (
          <button
            onClick={e => {
              e.stopPropagation();
              closeTab(tab.id);
            }}
            className={`
              -mr-1 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-primary-600
              transition-colors duration-150
              ${isActive ? 'text-accent-primary' : 'text-gray-400 dark:text-primary-400'}
            `}
            title="Tab schließen"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="relative flex items-center bg-gray-100 dark:bg-primary-800 border-b border-gray-300 dark:border-primary-600 h-[40px] z-30">
      {/* Logo */}
      <div className={`shrink-0 absolute top-0 left-0 flex items-center justify-center h-full bg-gray-100 dark:bg-primary-800 border-r border-gray-300 dark:border-primary-600 px-3 z-40 transition-all duration-300 ${sidebarWidth}`}>
        <img
          src={theme === 'dark' ? '/assets/gridpilot_logo_dark.svg' : '/assets/gridpilot_logo.svg'}
          alt="GridPilot"
          className={`h-5 w-auto object-contain ${isControlSidebarCollapsed ? 'hidden' : ''}`}
        />
      </div>

      {/* Spacer matching logo width */}
      <div className={`shrink-0 transition-all duration-300 ${sidebarWidth}`} />

      {/* Tabs — two boxes, "Alle Zonen" and the selected zone, exactly ONE of them highlighted: the
          one the user is in. Its chip is filled, the other box and its chip stay quiet — a zone
          name must never be the loudest thing in the bar while a page shows the whole grid. */}
      <div role="tablist" className="flex items-center gap-3 overflow-x-auto px-3 flex-1 min-w-0 h-full">
        {groups.map(group => {
          if (group.scope === 'app') {
            return (
              <div key={group.scope} className="flex items-center h-[32px] pl-1 border-l border-gray-300 dark:border-primary-600">
                {group.tabs.map(tab => renderTab(tab, 'normal'))}
              </div>
            );
          }
          const isActiveScope = group.scope === activeScope;
          return (
            <div
              key={group.scope}
              data-tab-scope={group.scope}
              data-active-scope={isActiveScope}
              className={`flex items-center h-[32px] pl-1 pr-0.5 rounded-lg border transition-colors duration-150 ${
                isActiveScope
                  ? 'bg-white border-accent-primary shadow-sm dark:bg-primary-700 dark:border-accent-primary'
                  : 'bg-transparent border-gray-300 dark:border-primary-600'
              }`}
            >
              {group.scope === 'fleet'
                ? <FleetScopeChip active={isActiveScope} onClick={() => openTab('landing')} />
                : <ZoneScopeChip active={isActiveScope} onClick={() => openTab('map')} />}
              {group.tabs.map(tab => renderTab(tab, group.scope === 'zone' && !hasActiveZone ? 'disabled' : isActiveScope ? 'normal' : 'quiet'))}
            </div>
          );
        })}
      </div>

      {/* Logout Button */}
      <button type="button" title="Hilfe zu dieser Ansicht" onClick={() => {
        if (activeTab?.type === 'documentation') useHelpStore.getState().selectArticle(null);
        else useHelpStore.getState().openPanel(activeTab?.type ?? 'landing');
      }} className="shrink-0 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 dark:text-gray-200 dark:hover:bg-primary-700">
        Hilfe
      </button>
      {showLogout && (
        <div className="shrink-0 relative border-l border-gray-300 dark:border-primary-600">
          <LogoutButton />
        </div>
      )}

      {/* Notification Bell */}
      <div className="shrink-0 relative border-l border-gray-300 dark:border-primary-600 px-2">
        <NotificationBell />
      </div>

      {/* Element search on the map tabs; the zone search lives in the dashboard / Netzmonitoring toolbar */}
      {headerSearch === 'element' && <ElementSearch />}
    </div>
  );
};
